import { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Play, Headphones, Clock, ChevronRight, ChevronLeft, CheckCircle, ListVideo, Plus, X, Search, GripVertical, Trash2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

const CATEGORIES = [
  { value: 'mental-training', label: 'Mental Training' },
  { value: 'pre-competition', label: 'Pre-Competition' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'focus', label: 'Focus & Concentration' },
  { value: 'confidence', label: 'Confidence Building' },
  { value: 'general', label: 'General' },
];

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];

const diffColor = (d) => ({
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-amber-100 text-amber-700',
  advanced: 'bg-red-100 text-red-700'
}[d] || 'bg-gray-100 text-gray-600');

// --- Playlist Card ---
function PlaylistCard({ playlist, onClick }) {
  const progress = playlist.user_progress || {};
  const completedCount = progress.completed_video_ids?.length || 0;
  const total = playlist.video_count || 0;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <Card
      data-testid={`playlist-card-${playlist.id}`}
      onClick={onClick}
      className="border-0 shadow-md bg-white hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
    >
      {/* Color band based on category */}
      <div className={`h-1.5 ${
        playlist.category === 'pre-competition' ? 'bg-red-500' :
        playlist.category === 'recovery' ? 'bg-blue-500' :
        playlist.category === 'focus' ? 'bg-purple-500' :
        playlist.category === 'confidence' ? 'bg-amber-500' :
        'bg-emerald-500'
      }`} />
      <CardHeader className="p-4">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <ListVideo className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-emerald-600 font-semibold uppercase">
              {(playlist.category || 'general').replace('-', ' ')}
            </span>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${diffColor(playlist.difficulty)}`}>
            {playlist.difficulty}
          </span>
        </div>
        <CardTitle className="text-base leading-tight line-clamp-2">{playlist.title}</CardTitle>
        <CardDescription className="text-xs line-clamp-2 mt-1">{playlist.description}</CardDescription>
        <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
          <span>{total} videos</span>
          <span>{playlist.estimated_minutes || 0} min</span>
          <span>by {playlist.created_by_name}</span>
        </div>
        {/* Progress bar */}
        {total > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-[10px] text-gray-500 mb-1">
              <span>{completedCount}/{total} completed</span>
              <span>{pct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${progress.completed ? 'bg-emerald-500' : 'bg-blue-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </CardHeader>
    </Card>
  );
}

// --- Playlist Detail View ---
function PlaylistDetail({ playlistId, onBack, onPlayVideo, user }) {
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetail();
  }, [playlistId]);

  const fetchDetail = async () => {
    try {
      const res = await axios.get(`${API}/videos/playlists/${playlistId}`, { headers: getAuth() });
      setPlaylist(res.data);
    } catch (e) {
      toast.error("Failed to load playlist");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (videoId) => {
    try {
      const res = await axios.post(
        `${API}/videos/playlists/${playlistId}/progress?video_id=${videoId}&completed_video=true`,
        {},
        { headers: getAuth() }
      );
      // Update local state
      setPlaylist(prev => ({
        ...prev,
        user_progress: {
          completed_video_ids: res.data.completed_video_ids,
          current_index: res.data.current_index,
          completed: res.data.completed
        }
      }));
      if (res.data.completed) {
        toast.success("Playlist completed!");
      }
    } catch (e) {
      toast.error("Failed to update progress");
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!playlist) return <div className="py-12 text-center text-gray-400">Playlist not found</div>;

  const videos = playlist.videos_detail || [];
  const progress = playlist.user_progress || {};
  const completedIds = progress.completed_video_ids || [];
  const completedCount = completedIds.length;
  const total = videos.length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <div>
      {/* Back button + header */}
      <button
        data-testid="playlist-back-btn"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Playlists
      </button>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${diffColor(playlist.difficulty)}`}>
                {playlist.difficulty}
              </span>
              <span className="text-xs text-gray-500 capitalize">{(playlist.category || '').replace('-', ' ')}</span>
            </div>
            <h2 data-testid="playlist-detail-title" className="text-2xl font-bold text-gray-900 mb-1">{playlist.title}</h2>
            <p className="text-sm text-gray-600 mb-3">{playlist.description}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>{total} videos</span>
              <span>{playlist.estimated_minutes || 0} min total</span>
              <span>by {playlist.created_by_name}</span>
            </div>
          </div>
          {progress.completed && (
            <div className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" /> Completed
            </div>
          )}
        </div>

        {/* Overall progress */}
        <div className="mt-5">
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>{completedCount} of {total} videos completed</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${progress.completed ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Video list */}
      <div className="space-y-2">
        {videos.map((video, idx) => {
          const isCompleted = completedIds.includes(video.id);
          const isCurrent = idx === progress.current_index && !progress.completed;
          return (
            <div
              key={video.id}
              data-testid={`playlist-video-${video.id}`}
              className={`flex items-center gap-4 p-4 rounded-xl transition-all cursor-pointer ${
                isCurrent ? 'bg-emerald-50 border border-emerald-200 shadow-sm' :
                isCompleted ? 'bg-gray-50' : 'bg-white shadow-sm hover:shadow-md'
              }`}
              onClick={() => onPlayVideo(video, idx)}
            >
              {/* Order number */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                isCompleted ? 'bg-emerald-500 text-white' :
                isCurrent ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-400' :
                'bg-gray-100 text-gray-500'
              }`}>
                {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
              </div>

              {/* Thumbnail */}
              <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 relative">
                <img
                  src={video.thumbnail || video.thumbnail_url || `https://placehold.co/200x130/1a1a2e/eaeaea?text=${idx + 1}`}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = `https://placehold.co/200x130/1a1a2e/eaeaea?text=${idx + 1}`; }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  {video.content_type === 'audio' || video.file_type === 'audio' ? (
                    <Headphones className="w-5 h-5 text-white/80" />
                  ) : (
                    <Play className="w-5 h-5 text-white/80" />
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium line-clamp-1 ${isCompleted ? 'text-gray-500' : 'text-gray-900'}`}>
                  {video.title}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <span>{video.duration_minutes || video.duration || '—'} min</span>
                  <span>{video.instructor || video.uploaded_by_name || ''}</span>
                </div>
              </div>

              {/* Action */}
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <span className="text-xs text-emerald-600 font-medium">Done</span>
                ) : isCurrent ? (
                  <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
                    <Play className="w-3 h-3" /> Play
                  </span>
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Create/Edit Playlist Dialog ---
function PlaylistEditor({ open, onClose, onSaved, editPlaylist, allVideos }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [difficulty, setDifficulty] = useState('beginner');
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videoSearch, setVideoSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editPlaylist) {
      setTitle(editPlaylist.title || '');
      setDescription(editPlaylist.description || '');
      setCategory(editPlaylist.category || 'general');
      setDifficulty(editPlaylist.difficulty || 'beginner');
      setSelectedVideos(
        (editPlaylist.videos_detail || editPlaylist.videos || []).map((v, i) => ({
          video_id: v.video_id || v.id,
          video_type: v.video_type || 'library',
          order: v.playlist_order ?? v.order ?? i,
          title: v.title || 'Video',
          duration: v.duration_minutes || v.duration || 0
        }))
      );
    } else {
      setTitle(''); setDescription(''); setCategory('general');
      setDifficulty('beginner'); setSelectedVideos([]);
    }
  }, [editPlaylist, open]);

  const filteredVideos = allVideos.filter(v => {
    if (!videoSearch.trim()) return true;
    const q = videoSearch.toLowerCase();
    return v.title?.toLowerCase().includes(q) || v.category?.toLowerCase().includes(q);
  });

  const addVideo = (video) => {
    if (selectedVideos.find(s => s.video_id === video.id)) return;
    setSelectedVideos(prev => [...prev, {
      video_id: video.id,
      video_type: 'library',
      order: prev.length,
      title: video.title,
      duration: video.duration_minutes || video.duration || 0
    }]);
  };

  const removeVideo = (videoId) => {
    setSelectedVideos(prev => prev.filter(v => v.video_id !== videoId).map((v, i) => ({ ...v, order: i })));
  };

  const moveVideo = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= selectedVideos.length) return;
    const arr = [...selectedVideos];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setSelectedVideos(arr.map((v, i) => ({ ...v, order: i })));
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (selectedVideos.length === 0) { toast.error("Add at least one video"); return; }
    setSaving(true);
    try {
      const payload = {
        title, description, category, difficulty,
        videos: selectedVideos.map(v => ({
          video_id: v.video_id, video_type: v.video_type, order: v.order
        }))
      };

      if (editPlaylist) {
        await axios.put(`${API}/videos/playlists/${editPlaylist.id}`, payload, { headers: getAuth() });
        toast.success("Playlist updated!");
      } else {
        await axios.post(`${API}/videos/playlists`, payload, { headers: { ...getAuth(), 'Content-Type': 'application/json' } });
        toast.success("Playlist created!");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save playlist");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="playlist-editor-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editPlaylist ? 'Edit Playlist' : 'Create Learning Path'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              data-testid="playlist-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g., Pre-Competition Mental Prep"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              data-testid="playlist-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="What will athletes learn from this path?"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                data-testid="playlist-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select
                data-testid="playlist-difficulty-select"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {DIFFICULTIES.map(d => <option key={d} value={d} className="capitalize">{d}</option>)}
              </select>
            </div>
          </div>

          {/* Selected Videos (ordered) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Videos in Path ({selectedVideos.length})
            </label>
            {selectedVideos.length > 0 ? (
              <div className="space-y-1.5 mb-3">
                {selectedVideos.map((v, idx) => (
                  <div key={v.video_id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                    <span className="text-xs font-bold text-gray-400 w-5 text-center">{idx + 1}</span>
                    <p className="text-sm text-gray-800 flex-1 truncate">{v.title}</p>
                    <span className="text-xs text-gray-400">{v.duration}m</span>
                    <div className="flex gap-0.5">
                      <button
                        type="button"
                        onClick={() => moveVideo(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronLeft className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveVideo(idx, 1)}
                        disabled={idx === selectedVideos.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronRight className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeVideo(v.video_id)}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 mb-3">No videos added yet. Search and add below.</p>
            )}

            {/* Video search */}
            <div className="border border-gray-200 rounded-lg">
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    data-testid="playlist-video-search"
                    type="text"
                    value={videoSearch}
                    onChange={(e) => setVideoSearch(e.target.value)}
                    placeholder="Search library videos to add..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs border-0 focus:outline-none"
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filteredVideos.slice(0, 20).map(v => {
                  const isAdded = selectedVideos.some(s => s.video_id === v.id);
                  return (
                    <div
                      key={v.id}
                      className={`flex items-center gap-2 px-3 py-2 text-xs border-b border-gray-50 last:border-0 ${isAdded ? 'bg-emerald-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}
                      onClick={() => !isAdded && addVideo(v)}
                    >
                      <span className="flex-1 truncate text-gray-800">{v.title}</span>
                      <span className="text-gray-400 flex-shrink-0">{v.duration_minutes || v.duration || 0}m</span>
                      {isAdded ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Plus className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
            <button
              data-testid="save-playlist-btn"
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Saving...' : editPlaylist ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Exports ---
export { PlaylistCard, PlaylistDetail, PlaylistEditor };
